import React, { useState, useEffect, useCallback } from 'react';
import { collection, addDoc, query, where, getDocs, orderBy, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { FaTimes, FaTools, FaCalendarAlt, FaMoneyBillWave, FaUserCog, FaClipboardList, FaCog } from 'react-icons/fa';

const MaintenanceModal = ({ vehicle, onClose, onMaintenanceAdded }) => {
  const [maintenanceRecords, setMaintenanceRecords] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    date: '',
    time: '',
    endTime: '',
    type: 'routine',
    description: '',
    cost: '',
    nextMaintenanceDate: '',
    mechanic: '',
    issues: '',
    partsReplaced: '',
    status: 'scheduled',
  });

  const fetchMaintenanceRecords = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const maintenanceRef = collection(db, 'maintenance');
      const q = query(
        maintenanceRef,
        where('vehicleId', '==', vehicle.id),
        orderBy('date', 'desc')
      );
      const snapshot = await getDocs(q);
      const records = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMaintenanceRecords(records);
    } catch (error) {
      console.error('Error fetching maintenance records:', error);
      setError('Failed to fetch maintenance records');
    } finally {
      setLoading(false);
    }
  }, [vehicle]);

  useEffect(() => {
    if (vehicle) {
      fetchMaintenanceRecords();
    }
  }, [vehicle, fetchMaintenanceRecords]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (editingRecord) {
        // Update existing record
        const maintenanceRef = doc(db, 'maintenance', editingRecord.id);
        await updateDoc(maintenanceRef, {
          ...formData,
          updatedAt: new Date().toISOString(),
        });
      } else {
        // Add new record
        const maintenanceRef = collection(db, 'maintenance');
        await addDoc(maintenanceRef, {
          ...formData,
          vehicleId: vehicle.id,
          vehicleRegistration: vehicle.registrationNumber,
          createdAt: new Date().toISOString(),
        });
      }
      
      // Update vehicle's last maintenance date
      const vehicleRef = doc(db, 'vehicles', vehicle.id);
      await updateDoc(vehicleRef, {
        lastMaintenanceDate: formData.date,
        nextMaintenanceDate: formData.nextMaintenanceDate,
      });

      setShowAddForm(false);
      resetForm();
      fetchMaintenanceRecords();
      if (onMaintenanceAdded) {
        onMaintenanceAdded();
      }
    } catch (error) {
      console.error('Error saving maintenance record:', error);
      setError('Failed to save maintenance record');
    }
  };

  const resetForm = () => {
    setFormData({
      date: '',
      time: '',
      endTime: '',
      type: 'routine',
      description: '',
      cost: '',
      nextMaintenanceDate: '',
      mechanic: '',
      issues: '',
      partsReplaced: '',
      status: 'scheduled',
    });
    setEditingRecord(null);
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({
      date: record.date,
      time: record.time || '',
      endTime: record.endTime || '',
      type: record.type || 'routine',
      description: record.description || '',
      cost: record.cost || '',
      nextMaintenanceDate: record.nextMaintenanceDate || '',
      mechanic: record.mechanic || '',
      issues: record.issues || '',
      partsReplaced: record.partsReplaced || '',
      status: record.status || 'scheduled',
    });
    setShowAddForm(true);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
        <div className="bg-white rounded-lg p-6 w-full max-w-4xl">
          <div className="text-center">Loading maintenance records...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-black">Maintenance Records - {vehicle?.registrationNumber}</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700" aria-label="Close modal">×</button>
          </div>
          {error && (
            <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded-lg mb-6">{error}</div>
          )}
          {!showAddForm ? (
            <div>
              <button
                onClick={() => setShowAddForm(true)}
                className="flex items-center gap-2 px-6 py-3 rounded-lg bg-[#00E0E0] text-white font-semibold hover:bg-[#00B8B8] transition-colors mb-6"
              >
                <FaTools /> Add Maintenance Record
              </button>
              {maintenanceRecords.map((record) => (
                <div key={record.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow mb-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold flex items-center gap-2">
                        <FaCog className="text-blue-500" />
                        {record.type.charAt(0).toUpperCase() + record.type.slice(1)} Maintenance
                      </h3>
                      <p className="text-sm text-gray-600 flex items-center gap-1">
                        <FaCalendarAlt className="text-gray-400" />
                        {new Date(record.date).toLocaleDateString()}
                        {record.time && ` at ${record.time}`}
                      </p>
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium mt-1 ${
                        (record.status || 'scheduled') === 'scheduled' ? 'bg-blue-100 text-blue-800' :
                        (record.status || 'scheduled') === 'completed' ? 'bg-green-100 text-green-800' :
                        (record.status || 'scheduled') === 'in-progress' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {(record.status || 'scheduled').charAt(0).toUpperCase() + (record.status || 'scheduled').slice(1)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEdit(record)}
                        className="text-blue-600 hover:text-blue-800 p-1"
                        title="Edit maintenance record"
                      >
                        <FaTimes className="transform rotate-45" />
                      </button>
                      {record.cost && (
                        <span className="text-sm font-medium flex items-center gap-1">
                          <FaMoneyBillWave className="text-green-500" />
                          ${record.cost}
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="flex items-start gap-2"><FaClipboardList className="text-gray-400 mt-1" /><span><span className="font-medium">Description:</span> {record.description}</span></p>
                  {record.issues && (<p className="flex items-start gap-2"><FaTools className="text-gray-400 mt-1" /><span><span className="font-medium">Issues Fixed:</span> {record.issues}</span></p>)}
                  {record.partsReplaced && (<p className="flex items-start gap-2"><FaCog className="text-gray-400 mt-1" /><span><span className="font-medium">Parts Replaced:</span> {record.partsReplaced}</span></p>)}
                  {record.mechanic && (<p className="flex items-center gap-2"><FaUserCog className="text-gray-400" /><span><span className="font-medium">Mechanic:</span> {record.mechanic}</span></p>)}
                  {record.nextMaintenanceDate && (<p className="flex items-center gap-2"><FaCalendarAlt className="text-gray-400" /><span><span className="font-medium">Next Maintenance:</span> {new Date(record.nextMaintenanceDate).toLocaleDateString()}</span></p>)}
                </div>
              ))}
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-800">
                  {editingRecord ? 'Edit Maintenance Record' : 'Add New Maintenance Record'}
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2"><FaCalendarAlt className="text-gray-400" />Maintenance Date</label>
                  <input type="date" required value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2"><FaCalendarAlt className="text-gray-400" />Start Time</label>
                  <input type="time" value={formData.time} onChange={(e) => setFormData({...formData, time: e.target.value})} className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2"><FaCalendarAlt className="text-gray-400" />End Time</label>
                  <input type="time" value={formData.endTime} onChange={(e) => setFormData({...formData, endTime: e.target.value})} className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2"><FaCog className="text-gray-400" />Type</label>
                  <select value={formData.type} onChange={(e) => setFormData({...formData, type: e.target.value})} className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]">
                    <option value="routine">Routine Maintenance</option>
                    <option value="repair">Repair</option>
                    <option value="inspection">Inspection</option>
                    <option value="emergency">Emergency</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2"><FaCog className="text-gray-400" />Status</label>
                  <select value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})} className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]">
                    <option value="scheduled">Scheduled</option>
                    <option value="completed">Completed</option>
                    <option value="in-progress">In Progress</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2"><FaClipboardList className="text-gray-400" />Description</label>
                  <textarea required value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]" rows="3" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2"><FaMoneyBillWave className="text-gray-400" />Cost</label>
                  <input type="number" required value={formData.cost} onChange={(e) => setFormData({...formData, cost: e.target.value})} className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]" placeholder="0.00" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2"><FaCalendarAlt className="text-gray-400" />Next Maintenance Date</label>
                  <input type="date" value={formData.nextMaintenanceDate} onChange={(e) => setFormData({...formData, nextMaintenanceDate: e.target.value})} className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2"><FaUserCog className="text-gray-400" />Mechanic</label>
                  <input type="text" value={formData.mechanic} onChange={(e) => setFormData({...formData, mechanic: e.target.value})} className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2"><FaTools className="text-gray-400" />Issues Fixed</label>
                  <textarea value={formData.issues} onChange={(e) => setFormData({...formData, issues: e.target.value})} className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]" rows="2" placeholder="Describe any mechanical issues that were fixed" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2"><FaCog className="text-gray-400" />Parts Replaced</label>
                  <textarea value={formData.partsReplaced} onChange={(e) => setFormData({...formData, partsReplaced: e.target.value})} className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]" rows="2" placeholder="List any parts that were replaced" />
                </div>
              </div>
              <div className="flex justify-end space-x-4 mt-6">
                <button 
                  type="button" 
                  onClick={() => { 
                    setShowAddForm(false); 
                    resetForm(); 
                  }} 
                  className="px-6 py-3 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="flex items-center space-x-2 px-6 py-3 rounded-lg bg-[#00E0E0] text-white font-semibold hover:bg-[#00B8B8] transition-colors"
                >
                  <FaTools />
                  <span>{editingRecord ? 'Update Record' : 'Save Record'}</span>
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default MaintenanceModal; 